import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { Input } from "../../share/ui/input";
import { cn } from "../../lib/utils";
import toast from "react-hot-toast";
import loginBg from "../../assets/img/login.png";
import maskgroup from "../../assets/img/maskgroup.png";
import logo from "../../assets/logo/evocare.svg";
import { useTranslation } from "react-i18next";
import { requestPasswordReset } from "../../api/login/authService";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<{ email?: string }>({});
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { email?: string } = {};
    if (!email) {
      newErrors.email = t("login.errorEmail");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      await requestPasswordReset(email);
      setErrors({});
      toast.success(t("toast.sendSuccessfully"));
      setTimeout(() => {
        navigate(`/enter-code?email=${encodeURIComponent(email)}`);
      }, 1500);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || error.message || t("toast.sendFailed");
      toast.error(errorMessage);
      setErrors({ email: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left content */}
      <div className="w-full md:w-3/5 flex items-start pt-16 justify-center bg-white md:px-8 px-4 relative">
        <img
          src={maskgroup}
          alt="Mask Group"
          width={450}
          className="absolute top-0 left-10 pointer-events-none z-10"
          style={{ height: "auto" }}
        />

        <div className="max-w-md w-full relative z-20">
          <img
            src={logo}
            alt="EvoCare Logo"
            width={230}
            height={60}
            className="mb-6"
            style={{ height: "auto" }}
          />

          <h2 className="text-xl font-semibold text-gray-900 mb-3">
            {t("login.forgotPassword")}
          </h2>
          <p className="text-sm text-gray-600 mb-6">{t("login.description")}</p>

          <form onSubmit={handleSend} className="space-y-6">
            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("login.email")}
              </label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors({});
                  }}
                  placeholder={t("login.enterEmail")}
                  className={cn(errors.email && "border-red-500 pr-10")}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email && (
                  <AlertCircle
                    className="absolute right-3 top-3 text-red-500"
                    size={18}
                    aria-hidden="true"
                  />
                )}
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm mt-1" id="email-error">
                  {errors.email}
                </p>
              )}
            </div>

            {/* Send button */}
            <button
              type="submit"
              className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-md font-medium shadow transition"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t("navigation.loading")}
                </span>
              ) : (
                t("button.send")
              )}
            </button>

            {/* Back button */}
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-full py-3 bg-orange-50 text-orange-700 hover:bg-orange-100 rounded-md font-medium shadow flex justify-center items-center gap-2 transition"
            >
              <ArrowLeft size={18} />
              <span>{t("button.back")}</span>
            </button>
          </form>
        </div>
      </div>

      {/* Right background image */}
      <div className="hidden md:block md:w-4/5 relative">
        <img
          src={loginBg}
          alt="Login Background"
          className="object-cover brightness-95 w-full h-full"
        />
      </div>
    </div>
  );
}
